import React from "react";
import { useFetcher } from "@remix-run/react";
import {
  Box,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  RangeSlider,
  ProgressBar,
  Banner,
  Button,
  Icon,
} from "@shopify/polaris";
import { TooltipDemoOnly as Tooltip } from "./TooltipDemoOnly";
import { CheckCircleIcon, ShieldCheckMarkIcon } from "@shopify/polaris-icons";
import { STARTER_PLAN } from "../constants";


export function AgentHealthCard({ settings, planName, threshold, onThresholdChange, activeJobs, initialImpact, fetcher: propFetcher, isReadOnly = false, liveCount, revalidator }: { settings: any, planName: string, threshold: number, onThresholdChange: (val: number) => void, activeJobs?: any[], initialImpact?: any, fetcher?: any, isReadOnly?: boolean, liveCount?: number, revalidator?: { revalidate: () => void, state: string } }) {
  const internalFetcher = useFetcher();
  const fetcher = propFetcher || internalFetcher;
  const impactFetcher = useFetcher<any>();
  const bulkFetcher = useFetcher<any>();
  const [localThreshold, setLocalThreshold] = React.useState(threshold);
  const handledJobIdRef = React.useRef<string | null>(null);

  const isBulkProcessing = !isReadOnly && (bulkFetcher.state === "submitting" || bulkFetcher.state === "loading");

  const shopify = typeof window !== "undefined" ? (window as any).shopify : null;


  const impactData = impactFetcher.data || initialImpact;
  // Use the client-side filtered count (liveCount) as the authoritative source.
  // This guarantees the button count matches the table count exactly.
  const pendingCount = liveCount ?? Number(impactData?.countCurrentlyPending ?? 0);
  // Only show progress for jobs that are actively running — ignore old FAILED/COMPLETED jobs
  const activeJob = activeJobs?.find(j => j.status === "ENQUEUED" || j.status === "PROCESSING") ?? null;
  const progressPercent = activeJob
    ? Math.round((activeJob.processedItems / (activeJob.totalItems || 1)) * 100)
    : 0;

  // Show failed job separately (last failed job, not an active one)
  const lastFailedJob = !activeJob ? activeJobs?.find(j => j.status === "FAILED_IN_QUEUE") ?? null : null;


  // Handle fetcher errors (threshold updates, etc.)
  React.useEffect(() => {
    const data = fetcher.data as any;
    if (data?.success === false && data?.error) {
      shopify?.toast.show(data.error, { isError: true });
    }
  }, [fetcher.data, shopify]);

  // Handle bulk action errors
  React.useEffect(() => {
    const data = bulkFetcher.data as any;
    if (data?.success === false && data?.error) {
      shopify?.toast.show(data.error, { isError: true });
    }
  }, [bulkFetcher.data, shopify]);

  // After bulk enqueue, show progress toast and schedule revalidation
  React.useEffect(() => {
    const data = bulkFetcher.data as any;
    if (!data?.success || !data?.jobId) return;
    
    // Don't handle the same job twice (fetcher may re-fire)
    if (handledJobIdRef.current === data.jobId) return;
    handledJobIdRef.current = data.jobId;

    const count = data.count || 0;
    shopify?.toast.show(
      `Processing ${count} returns — this may take a moment.`,
      { duration: 5000 }
    );

    // Schedule a revalidation after ~3 seconds to pick up job progress
    const timer = setTimeout(() => {
      revalidator?.revalidate();
      // Second revalidation after the job likely finishes
      setTimeout(() => {
        revalidator?.revalidate();
        handledJobIdRef.current = null;
      }, 3000);
    }, 3000);

    return () => clearTimeout(timer);
  }, [bulkFetcher.data, shopify, revalidator]);

  // Track external updates (initial load, etc.)

  const lastPropThreshold = React.useRef(threshold);
  React.useEffect(() => {
    if (threshold !== lastPropThreshold.current) {
      // Only override local state if the user hasn't moved the slider since the last prop update
      if (localThreshold === lastPropThreshold.current) {
        setLocalThreshold(threshold);
      }
      lastPropThreshold.current = threshold;
    }
  }, [threshold, localThreshold]);

  // Slider moves: only update local display — DO NOT submit anything yet
  const handleSliderChange = React.useCallback((value: number) => {
    setLocalThreshold(value);
  }, []);

  const lastProcessedThresholdRef = React.useRef(threshold);
  
  // Debounced SAVE threshold + fetch impact preview — fires only after slider stops
  React.useEffect(() => {
    if (localThreshold === threshold) return;
    // Prevent loop if we already triggered processing for this specific value
    if (localThreshold === lastProcessedThresholdRef.current) return;
    
    const timer = setTimeout(() => {
      lastProcessedThresholdRef.current = localThreshold;
      // Save the new threshold only if NOT read-only
      if (!isReadOnly) {
        onThresholdChange(localThreshold);
 
        // If no onThresholdChange was provided, or if it doesn't handle the fetcher submit, we do it here
        if (!propFetcher) {
          const saveForm = new FormData();
          saveForm.append("actionType", "UPDATE_SETTINGS");
          saveForm.append("threshold", localThreshold.toString());
          fetcher.submit(saveForm, { method: "post" });
        }
      }
 
      // Fetch impact preview after slider settles
      const impactForm = new FormData();
      impactForm.append("actionType", "GET_THRESHOLD_IMPACT");
      impactForm.append("threshold", localThreshold.toString());
      impactFetcher.submit(impactForm, { method: "post" });
    }, 600);
    return () => clearTimeout(timer);
  }, [localThreshold, threshold, fetcher, propFetcher, onThresholdChange, impactFetcher, isReadOnly]);

  // REFRESH IMPACT data and revalidate dashboard after bulk process completes
  React.useEffect(() => {
    if (bulkFetcher.state === "idle" && bulkFetcher.data?.success) {
      const currentJobId = bulkFetcher.data?.jobId;
      if (currentJobId && handledJobIdRef.current !== currentJobId) {
        handledJobIdRef.current = currentJobId;
        const impactForm = new FormData();
        impactForm.append("actionType", "GET_THRESHOLD_IMPACT");
        impactForm.append("threshold", localThreshold.toString());
        impactFetcher.submit(impactForm, { method: "post" });

        // Immediately revalidate dashboard so the processing queue indicator appears
        if (revalidator && revalidator.state === "idle") {
          revalidator.revalidate();
        }
      }
    }
  }, [bulkFetcher.state, bulkFetcher.data, localThreshold, impactFetcher, revalidator]);

  return (
    <div>
      <Card>
        <BlockStack gap="400">
          <InlineStack gap="200" blockAlign="center">
            <Icon source={ShieldCheckMarkIcon} tone="base" />
            <Text as="h2" variant="headingMd">
              Agent Health
            </Text>
          </InlineStack>
          <InlineStack align="space-between" blockAlign="center">
            <Text as="span">Status</Text>
            <Badge tone="success">Active</Badge>
          </InlineStack>
          <BlockStack gap="200">
            <InlineStack align="space-between">
              <Text as="span">Confidence Threshold</Text>
              <InlineStack gap="200" blockAlign="center">
                {planName === STARTER_PLAN && (
                  <Badge tone="warning">Upgrade to Growth+</Badge>
                )}

                <span data-testid="confidence-threshold-value">
                  <Text as="span" fontWeight="bold">
                    {Math.round(localThreshold * 100)}%
                  </Text>
                </span>
              </InlineStack>
            </InlineStack>
            <div data-testid="confidence-threshold-slider" style={{ pointerEvents: isReadOnly ? 'none' : 'auto', opacity: isReadOnly ? 1 : undefined }}>
              <Tooltip content="Higher thresholds are safer (AI only acts when certain). Lower thresholds automate more, but may need more review.">
                <RangeSlider
                  label="Adjust confidence threshold"
                  labelHidden
                  value={localThreshold}
                  onChange={handleSliderChange}
                  min={0.5}
                  max={1.0}
                  step={0.01}
                  output
                  disabled={planName === STARTER_PLAN}
                />
              </Tooltip>
            </div>
            {/* Show Impact Preview box if we have live items OR backend data OR read-only mode */}
            {(pendingCount > 0 || impactData?.success || isReadOnly) && (
              <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                <BlockStack gap="100">
                  <InlineStack align="space-between">
                    <Text variant="bodySm" fontWeight="bold" as="p">Impact Preview</Text>
                    <Icon source={ShieldCheckMarkIcon} tone="info" />
                  </InlineStack>

                  <div style={{ minHeight: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    {impactFetcher.state === "loading" ? (
                      <Box paddingBlockStart="100">
                        <Text variant="bodySm" tone="subdued" as="p">Calculating impact...</Text>
                      </Box>
                    ) : (
                      <>
                        {/* 30-day historical stat — only shown after backend responds */}
                        {impactData?.success ? (
                          <Text variant="bodySm" as="p" data-testid="impact-preview-text">
                            At <b>&gt;{Math.round(localThreshold * 100)}%</b>, this would have automated <b>{impactData.countLast30Days ?? 0}</b> returns in the last 30 days.
                          </Text>
                        ) : (
                          <Text variant="bodySm" tone="subdued" as="p">Adjust the slider above to see the impact of this threshold on your returns.</Text>
                        )}
                      </>
                    )}
                  </div>

                      {(pendingCount > 0 || isReadOnly) && (
                        <BlockStack gap="200">
                          <div data-testid="pending-count-text">
                            <Text variant="bodySm" tone="success" as="p">
                              <>There are <b>{pendingCount}</b> Live Feed returns ready for bulk processing.</>
                            </Text>
                          </div>

                          {!activeJob && (
                            <div style={{ marginTop: '8px' }}>
                              <div data-testid="bulk-process-btn" className="approve-btn">
                                <Tooltip content="Automate all pending returns that meet your confidence threshold in one click.">
                                  <Button
                                    variant="primary"
                                    size="slim"
                                    loading={isBulkProcessing}
                                    onClick={() => {
                                      if (isReadOnly) {
                                        const shopify = (window as any).shopify;
                                        shopify?.toast.show("Recovery simulated! In a live environment, these returns would be processed immediately.", { duration: 3000 });
                                        return;
                                      }
                                      const formData = new FormData();
                                      formData.append("actionType", "BULK_PROCESS_BY_THRESHOLD");
                                      formData.append("threshold", localThreshold.toString());
                                      bulkFetcher.submit(formData, { method: "post" });
                                    }}
                                    disabled={isReadOnly}
                                  >
                                    {`Process ${pendingCount} Live Feed Returns`}
                                  </Button>
                                </Tooltip>
                              </div>
                            </div>
                          )}
                        </BlockStack>
                      )}

                  {activeJob && (
                    <Box paddingBlockStart="200">
                      <BlockStack gap="300">
                        <BlockStack gap="100">
                          <InlineStack align="space-between">
                            <Text variant="bodySm" tone="subdued" as="p">
                              Processing Queue (Remaining: {activeJob.totalItems - activeJob.processedItems})
                            </Text>
                            <Text variant="bodySm" fontWeight="bold" as="p">{progressPercent}%</Text>
                          </InlineStack>
                          <ProgressBar progress={progressPercent} size="small" tone="highlight" data-testid="bulk-process-progress" />
                        </BlockStack>
                      </BlockStack>
                    </Box>
                  )}

                  {lastFailedJob && (
                    <BlockStack gap="200">
                      <Banner tone="critical">
                        <p>{lastFailedJob.error || "A background error occurred. Please retry below."}</p>
                      </Banner>
                      <bulkFetcher.Form method="post">
                        <input type="hidden" name="actionType" value="RETRY_FAILED_JOB" />
                        <input type="hidden" name="jobId" value={lastFailedJob.id} />
                        <Tooltip content="Retries processing for any items that encountered background errors during the bulk operation.">
                          <div className="approve-btn">
                            <Button variant="primary" submit loading={isBulkProcessing} disabled={isReadOnly}>
                              Retry Failed Items
                            </Button>
                          </div>
                        </Tooltip>
                      </bulkFetcher.Form>
                    </BlockStack>
                  )}
                </BlockStack>
              </Box>
            )}
            <Text variant="bodySm" tone="subdued" as="p">
              Higher thresholds are safer (less churn risk) but automate fewer returns. Lower thresholds increase automation but may require more manual oversight.
            </Text>
          </BlockStack>
          <InlineStack align="space-between" blockAlign="center">
            <Text as="span">Recovery Engine</Text>
            <InlineStack gap="100" blockAlign="center">
              <Icon source={CheckCircleIcon} tone="success" />
              <Text as="span" fontWeight="bold">Gemini Enterprise</Text>
            </InlineStack>
          </InlineStack>
        </BlockStack>
      </Card>
    </div>
  );
}
